import React, { useEffect, useState } from 'react';
import { Box, Fab, Grid, Toolbar, Tooltip } from '@mui/material';
import { useWizardStepperContext, StepperEnum } from '../../../../contexts/useWizardStepper.context';
import IReportFilterCriteria from '../../../../../../../shared/contracts/IReportFilterCriteria';
import useWizardReportDataContext from '../../../../contexts/useWizardReportData.context';
import FilterFieldDialogComponent from '../../../../../../pages/reporting-and-analytics/report-data/components/report-data-filters-dialog/components/filter-field-dialog/filter-field-dialog.component';
import { FILTER_CATEGORIES } from '../../../../../../../shared/contracts/TFilterCategory';
import FiltersComponent from '../../../../../../pages/reporting-and-analytics/report-data/components/report-data-filters-dialog/components/filters/filters.component';
import MuiIcon from '../../../../../../../shared/styles/mui/helpers/mui-icon.helper';
import CallOutComponent from '../../../callout/callout.component';
import useHelpers from '../../../../../../../shared/services/helpers/useHelpers.hook';
import WizardAddNewFilterButton from './wizard-add-new-filter.button';

interface IProps {

}
/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function WizardReportFiltersComponent() {

    const { checkIsDifferentOnSameType } = useHelpers();
    const { activeStep } = useWizardStepperContext();
    const { wizardData, applyReportData, availableColumns } = useWizardReportDataContext();
    const [targetFilterItem, setTargetFilterItem] = useState<IReportFilterCriteria | null>(null);
    const [targetFilterIndex, setTargetFilterIndex] = useState<number>(-1);
    const [listFilters, setListFilters] = useState<IReportFilterCriteria[]>([]);
    const [init, setInit] = useState<boolean>(true);

    const hasChanges = () => {
        return checkIsDifferentOnSameType(listFilters,
            wizardData.reportSpecification.filterCriterias)
    }

    useEffect(() => {
        // mark the flag
        if (init) { setInit(false); }
    }, [])

    useEffect(() => {
        // initialize onLoad or onReset
        if (hasChanges()) {
            setListFilters([...wizardData.reportSpecification.filterCriterias]);
        }
    }, [wizardData.reportSpecification.filterCriterias])

    useEffect(() => {
        // update on Change the wizard-data
        if (!init && hasChanges()) {
            applyReportData({
                reportSpecification: {
                    ...wizardData.reportSpecification,
                    filterCriterias: [...listFilters]
                }
            });
        }
    }, [init, listFilters])

    const handleDialogCancel = () => {
        setTargetFilterIndex(-1);
        setTargetFilterItem(null);
    }

    const handleAddNewFilterItem = () => {
        setTargetFilterIndex(-1);
        setTargetFilterItem({
            column: '', operator: '', aliasTable: '',
            description: '', guid: '', value: '', operatorValueId: 0,
            columnDescription: null
        });
    }

    const handleEditClick = (filterIndex: number, filterItem: IReportFilterCriteria) => {
        setTargetFilterIndex(filterIndex);
        setTargetFilterItem({ ...filterItem });
    }

    const handleDeleteClick = (
        filterIndex: number,
        filterItem: IReportFilterCriteria
    ) => {
        const _filters = [...listFilters]
        _filters.splice(filterIndex, 1);
        setListFilters(_filters);
    }

    const handleFilterItemChange = (
        filterIndex: number,
        targetFilterItem: IReportFilterCriteria
    ) => {
        if (filterIndex === -1) {
            setListFilters([...listFilters, targetFilterItem]);
        } else {
            const _listFilters = [...listFilters];
            _listFilters.splice(filterIndex, 1, targetFilterItem);
            setListFilters([..._listFilters]);
        }
        // closes the dialog
        handleDialogCancel();
    }

    const handleFilterValueChange = (
        filterIndex: number,
        filterValue: string | null
    ) => {
        const _listFilters = [...listFilters];
        const targetFilterItem = {
            ..._listFilters[filterIndex],
            value: filterValue,
        };

        _listFilters[filterIndex] = targetFilterItem;
        setListFilters([..._listFilters]);
    };

    return (<Box hidden={activeStep !== StepperEnum.Filters}
        sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}
    >

        {/* Fixed Header */}
        <Toolbar variant='dense'>
            <Grid container justifyContent='center'>

                {/* Placeholder */}
                <Grid item xs={2}>
                </Grid>

                {/* Empty-Information */}
                <Grid item xs={8}>
                    {listFilters.length <= 0 && <Grid item container direction='row' justifyContent='center' alignItems='center' sx={{ pt: 2 }}>
                        <CallOutComponent type="warning" text="No filters have been applied. Please click on 'Add New Filter' button to apply filters." />
                    </Grid>}
                </Grid>

                {/* FAB - Add New Filter */}
                <Grid item xs={2}>
                    {listFilters.length > 0 && <Tooltip title='Add New Filter' placement='left'>
                        <Fab color='info' onClick={handleAddNewFilterItem}
                            sx={{ position: 'absolute', top: 16, right: 28, }}
                        >
                            {MuiIcon.ADD()}
                        </Fab>
                    </Tooltip>}
                </Grid>

            </Grid>
        </Toolbar>

        {/* Add Filter Button */}
        {listFilters.length <= 0 && <Grid container justifyContent='center'
            sx={{ height: 'calc(100% - 80px)' }}>
            <WizardAddNewFilterButton onClick={handleAddNewFilterItem} />
        </Grid>}


        {/* Scrollable Dynamic List */}
        {listFilters.length > 0 && <Box sx={{
            overflowY: 'auto', pt: 0, pr: 2, pl: 8, pb: 2,
            minHeight: '64px', maxHeight: 'calc(100% - 50px)'

        }}>
            {/* Filters List */}
            <FiltersComponent containerSx={{ pb: 3 }}
                showAddNewButton={false} listFilters={listFilters}
                handleAddNewFilterItem={handleAddNewFilterItem}
                handleEditClick={handleEditClick}
                handleDeleteClick={handleDeleteClick}
                onFilterValueChange={handleFilterValueChange}
            />
        </Box>}

        {/* Dialog - Add/Edit */}
        {targetFilterItem && <FilterFieldDialogComponent
            targetFilterIndex={targetFilterIndex} targetFilterItem={targetFilterItem}
            defaultAvailableColumns={availableColumns}
            filterCategories={FILTER_CATEGORIES}
            onApply={handleFilterItemChange} onCancel={handleDialogCancel} />
        }

    </Box>);
}